import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { getNativeInternalPath } from "../../native/appLinkRouting";
import { getNativePlatform, isNativeApp, openExternalUrl } from "../../native/runtime";

const ENDPOINT = "/notifications/platform-notice/";
const SEEN_STORAGE_KEY = "vg_platform_notice_seen";
const PENDING_PUSH_KEY = "vg_platform_notice_pending_push";
const POLL_MS = 90_000;

function readSeen() {
  try {
    const value = JSON.parse(window.localStorage.getItem(SEEN_STORAGE_KEY) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

function rememberSeen(notice) {
  try {
    const seen = readSeen();
    seen[notice.slug] = notice.revision;
    const entries = Object.entries(seen).slice(-50);
    window.localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // The current session still remembers the notice when storage is unavailable.
  }
}

function storedSeenKeys() {
  return Object.entries(readSeen()).slice(-50).map(([slug, revision]) => `${slug}:${revision}`).join(",");
}

function takePendingPush() {
  try {
    const slug = window.sessionStorage.getItem(PENDING_PUSH_KEY) || "";
    if (slug) window.sessionStorage.removeItem(PENDING_PUSH_KEY);
    return slug;
  } catch {
    return "";
  }
}

export default function PlatformNoticeController({ language = "en" }) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [notice, setNotice] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [updateBlocked, setUpdateBlocked] = useState(
    () => isNativeApp() && window.__VG_MOBILE_UPDATE_VISIBLE__ !== false
  );
  const [actionError, setActionError] = useState(false);
  const currentRef = useRef(null);
  const shownRef = useRef(new Set());
  const seenRef = useRef(new Set());
  const requestRef = useRef(0);

  const record = useCallback((item, action) => {
    if (!authenticated) return;
    void api.post(`${ENDPOINT}${encodeURIComponent(item.slug)}/interaction/`, {
      revision: item.revision,
      platform: getNativePlatform(),
      action,
    }, { meta: { suppressGlobalError: true, suppressUnauthorizedRedirect: true } })
      .catch((error) => console.warn("Platform notice interaction could not be recorded", error));
  }, [authenticated]);

  const refresh = useCallback(async (forcedSlug = "") => {
    const sequence = ++requestRef.current;
    const current = currentRef.current;
    const slug = forcedSlug || current?.slug || "";
    try {
      const { data } = await api.get(ENDPOINT, {
        params: { platform: getNativePlatform(), ...(slug ? { slug } : {
          seen: storedSeenKeys(),
          session_seen: [...seenRef.current].slice(-50).join(","),
        }) },
        meta: { authOptional: true, suppressGlobalError: true, suppressUnauthorizedRedirect: true },
      });
      if (sequence !== requestRef.current) return;
      setAuthenticated(Boolean(data?.authenticated));
      const next = data?.notice || null;
      currentRef.current = next;
      setNotice(next);
      setActionError(false);
    } catch (error) {
      console.warn("Platform notice check failed", error);
    }
  }, []);

  useEffect(() => {
    void refresh(takePendingPush());
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    const onResume = () => void refresh();
    const onAuthChanged = () => {
      shownRef.current.clear();
      seenRef.current.clear();
      currentRef.current = null;
      setNotice(null);
      void refresh();
    };
    const onPush = (event) => {
      const slug = String(event?.detail?.slug || "");
      if (slug) void refresh(slug);
    };
    const onUpdateState = (event) => setUpdateBlocked(Boolean(event?.detail?.visible));
    window.addEventListener("vg:native-resume", onResume);
    window.addEventListener("vg:native-auth-changed", onAuthChanged);
    window.addEventListener("vg:platform-notice-push", onPush);
    window.addEventListener("vg:mobile-update-state", onUpdateState);
    return () => {
      requestRef.current += 1;
      window.clearInterval(timer);
      window.removeEventListener("vg:native-resume", onResume);
      window.removeEventListener("vg:native-auth-changed", onAuthChanged);
      window.removeEventListener("vg:platform-notice-push", onPush);
      window.removeEventListener("vg:mobile-update-state", onUpdateState);
    };
  }, [refresh]);

  const visible = Boolean(notice && !updateBlocked);
  useEffect(() => {
    if (!visible) return;
    const key = `${notice.slug}:${notice.revision}`;
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    seenRef.current.add(key);
    if (authenticated) record(notice, "shown");
    else rememberSeen(notice);
  }, [authenticated, notice, record, visible]);

  const close = (action = "dismissed") => {
    if (!notice) return;
    record(notice, action);
    currentRef.current = null;
    setNotice(null);
    void refresh();
  };

  const handleAction = async (which) => {
    if (!notice) return;
    const action = which === "secondary" ? notice.secondary_action : notice.primary_action;
    if (!action) return;
    if (action.type === "dismiss") {
      close(which);
      return;
    }
    if (action.type === "internal") {
      const target = String(action.target || "");
      const safePath = isNativeApp() ? getNativeInternalPath(target) :
        (target.startsWith("/") && !target.startsWith("//") && !target.includes("\\") ? target : null);
      if (!safePath) { setActionError(true); return; }
      close(which);
      navigate(safePath);
      return;
    }
    if (action.type === "external") {
      const target = String(action.target || "");
      try {
        if (new URL(target).protocol !== "https:") throw new Error("HTTPS URL required");
        await openExternalUrl(target);
        close(which);
      } catch {
        setActionError(true);
      }
    }
  };

  if (!notice) return null;
  const locale = String(language || "en").toLowerCase().split("-")[0];
  const copy = notice.translations?.[locale] || notice.translations?.en;
  if (!copy) return null;
  return (
    <AppModalShell
      open={visible}
      title={copy.title}
      description={copy.body}
      descriptionClassName="whitespace-pre-wrap text-center"
      onClose={() => close()}
      closeAriaLabel={t("buttons.close")}
      maxWidthClassName="max-w-[480px]"
      zIndexClassName="z-[14000]"
      forcePortalToBody
      footer={(
        <div className="space-y-2.5">
          <AppModalButton onClick={() => void handleAction("primary")}>
            {copy.primary_label}
          </AppModalButton>
          {notice.secondary_action ? (
            <AppModalButton variant="secondary" onClick={() => void handleAction("secondary")}>
              {copy.secondary_label || notice.translations?.en?.secondary_label}
            </AppModalButton>
          ) : null}
        </div>
      )}
    >
      {actionError ? (
        <p role="alert" className="text-center text-sm text-red-200">
          {t("errors.generic", "Could not open the link. Please try again.")}
        </p>
      ) : null}
    </AppModalShell>
  );
}
